import React from 'react';
import { Typography, Tooltip } from 'antd';
import { getHealthScoreColor } from '../utils/healthColorUtils';

const { Text } = Typography;

const ParameterVisualization = ({ zScore, currentValue, idealTarget, unit = '', normalRange, sd }) => {
  const minZ = -15;
  const maxZ = 15;
  const totalZRange = maxZ - minZ;

  // Helper to convert a raw value to its Z-score
  const valueToZScore = (value) => {
    if (typeof value !== 'number' || typeof idealTarget !== 'number' || !sd) {
      return null;
    }
    return (value - idealTarget) / sd;
  };

  // Helper to convert a Z-score to a percentage position on the bar
  const zScoreToPercentage = (z) => {
    if (typeof z !== 'number') return 0;
    const clamped = Math.max(minZ, Math.min(maxZ, z));
    return ((clamped - minZ) / totalZRange) * 100;
  };

  const currentPercentage = zScoreToPercentage(zScore);

  const getHealthScoreFromZScore = (z) => {
    const absZ = Math.abs(z);
    if (absZ <= 1) return 85;
    if (absZ <= 2) return 65;
    if (absZ <= 3) return 45;
    return 25;
  };

  const getMarkerColor = (z) => {
    const healthScore = getHealthScoreFromZScore(z);
    return getHealthScoreColor(healthScore);
  };

  const renderNormalRange = () => {
    if (!normalRange || !Array.isArray(normalRange) || typeof idealTarget !== 'number' || !sd) {
      return null;
    }
  
    const [lowerBound, upperBound] = normalRange;
  
    const lowerZ = valueToZScore(lowerBound);
    const upperZ = valueToZScore(upperBound);
  
    const lowerPercent = lowerBound !== null ? zScoreToPercentage(lowerZ) : null;
    const upperPercent = upperBound !== null ? zScoreToPercentage(upperZ) : null;
  
    const renderMarker = (percent, boundValue, type) => {
      if (percent === null || percent < 0 || percent > 100) return null;
  
      const tooltipTitle = `Normal Range (${type}): ${boundValue} ${unit}`;
  
      return (
        <Tooltip title={tooltipTitle} placement="top">
          <div style={{
            position: 'absolute',
            left: `${percent}%`,
            transform: 'translateX(-50%)',
            top: '-2px',
            bottom: '-2px',
            width: '2px',
            backgroundColor: 'rgba(0, 128, 0, 0.6)', // A darker, more visible green
            zIndex: 1,
          }} />
        </Tooltip>
      );
    };
  
    return (
      <>
        {renderMarker(lowerPercent, lowerBound, 'Lower')}
        {renderMarker(upperPercent, upperBound, 'Upper')}
      </>
    );
  };
  
  const renderTooltipTitle = (value, type) => (
    <div>
      <div><strong>{type}:</strong> {typeof value === 'number' ? value.toFixed(2) : 'N/A'} {unit}</div>
      {normalRange && Array.isArray(normalRange) && (
        <div><strong>Normal Range:</strong> {normalRange[0] !== null ? normalRange[0].toFixed(2) : '<'} - {normalRange[1] !== null ? normalRange[1].toFixed(2) : '>'} {unit}</div>
      )}
    </div>
  );

  return (
    <div style={{ width: '100%', padding: '0 10px' }}>
      {/* Visualization bar with markers */}
      <div style={{
        position: 'relative',
        height: '12px',
        background: '#f0f0f0',
        borderRadius: '6px',
        border: '1px solid #d9d9d9',
      }}>
        {renderNormalRange()}
        
        {/* Ideal Target Marker - positioned at center */}
        <Tooltip title={renderTooltipTitle(idealTarget, 'Ideal Target')} placement="top">
          <div style={{
            position: 'absolute',
            left: `${zScoreToPercentage(0)}%`,
            transform: 'translateX(-50%)',
            top: '-4px',
            bottom: '-4px',
            width: '4px',
            backgroundColor: 'rgba(24, 144, 255, 0.8)',
            borderRadius: '2px',
            cursor: 'pointer',
            zIndex: 1,
          }} />
        </Tooltip>

        {/* Current Value Marker */}
        <Tooltip title={renderTooltipTitle(currentValue, 'Current Value')} placement="top">
          <div
            style={{
              position: 'absolute',
              left: `${Math.max(2, Math.min(98, currentPercentage))}%`,
              transform: 'translateX(-50%)',
              top: '-6px',
              height: '24px',
              width: '8px',
              backgroundColor: getMarkerColor(zScore),
              borderRadius: '4px',
              border: '1px solid rgba(0,0,0,0.2)',
              cursor: 'pointer',
              zIndex: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          />
        </Tooltip>
      </div>

      {/* Bottom scale labels */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '4px',
        padding: '0 5px',
      }}>
        <Text type="secondary" style={{ fontSize: '10px' }}>{minZ}σ</Text>
        <Text type="secondary" style={{ fontSize: '10px', fontWeight: 'bold' }}>0σ</Text>
        <Text type="secondary" style={{ fontSize: '10px' }}>{maxZ}σ</Text>
      </div>
    </div>
  );
};

export default ParameterVisualization;
